import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import {
  ExclamationCircleOutlined,
  AppstoreAddOutlined,
  PlusSquareOutlined,
  NodeIndexOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Form, Modal, message } from "antd";
import { connect } from "dva";
import { Integrations, TrailerType, NissanVehicleStatus } from "@/utils/enum";
import { getAllStates } from "@/services/addresses";
import { getVehicles, changeStatusVehicle } from "@/services/nissanVehicles";
import { createShipmentFromTenderFile } from "@/services/shipments_new";
import ListView from "@/components/ListView";
import { ListToBeSorted } from "@/components/SortAndFilter";
import UpdateBayLocationsForm from "@/pages/WWLNissan/TenderFileInput/components/UpdateBayLocationsForm";
import useTenderFileInputTableColumns from "./utils/useTenderFileInputTableColumns";

const { confirm } = Modal;

const TenderInputFileProcess = {
  CreateMultiVehicleShipment: 1,
  CreateShipments: 2,
  ShipManually: 3,
};

const TenderFileInput = ({
  dispatch,
  configPerm,
  loadingCreateShipment,
  loadingShipManually,
  uploadingBayLocationSpreadsheet,
  location,
}) => {
  const actionRef = useRef();
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [isSingleVehicle, setIsSingleVehicle] = useState(true);
  const [isMultiVehicleShipmentDisabled, setIsMultiVehicleShipmentDisabled] = useState(true);
  const [isCreateShipmentsDisabled, setIsCreateShipmentsDisabled] = useState(true);
  const [isShipManuallyDisabled, setIsShipManuallyDisabled] = useState(true);

  const [bayLocationForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [process, setProcess] = useState(null);
  const [bayLocationModalVisible, setBayLocationModalVisible] = useState(false);
  const [states, setStates] = useState([]);

  const columns = useTenderFileInputTableColumns();

  const getStateFromShortName = useCallback(
    async stateShortName => {
      let localStates = states;

      if (localStates.length === 0) {
        try {
          localStates = (await getAllStates()).items || [];
          setStates(localStates);
        } catch {
          return null;
        }
      }

      return localStates.find(state => state.shortName === stateShortName);
    },
    [states],
  );

  const createShipmentLevelPayload = useCallback(row => {
    const { dealer, sequenceNumber, vpcLocation } = row || {};

    const { vpc, railhead } = vpcLocation || {};
    const { code: dealerCode } = dealer || {};

    return {
      dealerIntegrationId: vpcLocation.id,
      requiresTWIC: false,
      requiresEModal: false,
      requiresLotExit: true,
      trailerType: TrailerType.Open,
      integrationType: Integrations.WWL,
      vpc: vpc?.toString(),
      railHead: railhead?.toString(),
      carrierCode: dealerCode,
      sequenceNumber: sequenceNumber ?? "123",
      isAvailable: true,
    };
  }, []);

  const createAddressesPayload = useCallback(
    async row => {
      const {
        dealer,
        dropOffName,
        dropOffAddress,
        dropOffCity,
        dropOffState,
        dropOffZipCode,
        pickupName,
        pickupAddress,
        pickupCity,
        pickupState,
        pickupZipCode,
      } = row || {};

      const { name: dealerName, phoneNumber: dealerPhone } = dealer || {};

      const pickupAddressFinal = {
        name: pickupName,
        addressLine1: pickupAddress,
        city: pickupCity,
        state: await getStateFromShortName(pickupState),
        zipCode: pickupZipCode?.toString()?.padStart(5, "0"),
        contactName: dealerName ?? "1",
        contactPhone: dealerPhone ?? "1",
      };

      const dropOffAddressFinal = {
        name: dropOffName,
        addressLine1: dropOffAddress,
        city: dropOffCity,
        state: await getStateFromShortName(dropOffState),
        zipCode: dropOffZipCode?.toString()?.padStart(5, "0"),
        contactName: "1",
        contactPhone: "1",
      };

      return {
        pickupAddress: pickupAddressFinal,
        dropOffAddress: dropOffAddressFinal,
      };
    },
    [getStateFromShortName],
  );

  const createVehiclePayload = useCallback(row => {
    const { vin, bayLocation, color, id, model, transporterPrice, shipperPrice } = row || {};

    return {
      vin,
      model,
      color,
      comments: `Bay Location: ${bayLocation}`,
      dealerPrice: shipperPrice,
      transporterPrice,
      integrationVehicleId: id,
    };
  }, []);

  const generateResultsMessage = useCallback(results => {
    const fulfilled = [];
    const rejected = [];
    results.forEach(({ status, value, reason }) => {
      if (status === "rejected") {
        rejected.push(reason ?? "error");
      } else if (status === "fulfilled") {
        fulfilled.push(value);
      }
    });

    if (results.length === 1 && fulfilled.length === 1) {
      message.success(`Successfully created a shipment with ${results.length} vehicles added to it.`, 8);

      return;
    }

    if (fulfilled.length > 0) {
      message.success(`Successfully processed ${fulfilled.length} vehicle(s).`, 8);
    }

    if (rejected.length > 0) {
      // we only look for the specific WWL error
      const dealerIntegrationError = rejected.filter(error => error.message.includes("WWL"))?.at(0) ?? "";

      message.error(`Failed to process ${rejected.length} vehicle(s). ${dealerIntegrationError}`, 8);
    }
  }, []);

  const fetchVehicleList = useCallback(async ({ filters }) => {
    try {
      const response = await getVehicles(filters);

      return {
        data: response?.items ?? [],
        total: response?.totalCount || 0,
        success: true,
      };
    } catch (error) {
      return {
        data: [],
        total: 0,
      };
    }
  }, []);

  const createMultiVehicleShipmentConfirm = useCallback(() => {
    confirm({
      title: "Are you sure?",
      icon: <ExclamationCircleOutlined />,
      content: "This will create a single shipment and add all selected vehicles to it in the system.",
      onOk: async () => {
        if (isMultiVehicleShipmentDisabled) {
          return;
        }

        setProcess(TenderInputFileProcess.CreateMultiVehicleShipment);
        setLoading(true);

        const vehicle = selectedVehicles?.[0] || {};

        const { pickupAddress, dropOffAddress } = await createAddressesPayload(vehicle);

        const payload = {
          ...createShipmentLevelPayload(vehicle),
          pickupAddress,
          dropOffAddress,
          vehicles: selectedVehicles.map(v => createVehiclePayload(v)),
        };

        dispatch({
          type: "wwlNissan/createShipment",
          payload,
        })
          .then(() => {
            message.success(`Successfully created a shipment with ${selectedVehicles.length} vehicles added to it.`, 8);

            setSelectedVehicles([]);
            actionRef?.current?.reload();
          })
          .catch(error => {
            message.error("Failed to process your request.");
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  }, [
    createAddressesPayload,
    createShipmentLevelPayload,
    createVehiclePayload,
    selectedVehicles,
    isMultiVehicleShipmentDisabled,
    dispatch,
  ]);

  const createShipments = useCallback(() => {
    confirm({
      title: "Are you sure?",
      icon: <ExclamationCircleOutlined />,
      content: `This will create ${isSingleVehicle ? "a" : ""} shipment${isSingleVehicle ? "" : "s"} and vehicle${
        isSingleVehicle ? "" : "s"
      } in the system ${isSingleVehicle ? "" : "separately"} using the selected information.`,
      onOk: () => {
        setLoading(true);
        setProcess(TenderInputFileProcess.CreateShipments);

        Promise.allSettled(
          selectedVehicles.map(async vehicle => {
            const { pickupAddress, dropOffAddress } = await createAddressesPayload(vehicle);
            const vehicles = [createVehiclePayload(vehicle)];

            const payload = {
              ...createShipmentLevelPayload(vehicle),
              pickupAddress,
              dropOffAddress,
              vehicles,
            };

            return createShipmentFromTenderFile(payload);
          }),
        )
          .then(results => {
            generateResultsMessage(results);
            setSelectedVehicles([]);
            actionRef?.current?.reload();
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  }, [
    selectedVehicles,
    createAddressesPayload,
    createVehiclePayload,
    createShipmentLevelPayload,
    generateResultsMessage,
    isSingleVehicle,
  ]);

  const shipManuallyConfirm = useCallback(() => {
    confirm({
      title: "Are you sure?",
      icon: <ExclamationCircleOutlined />,
      content: `This will remove ${isSingleVehicle ? "a" : ""} vehicle${
        isSingleVehicle ? "" : "s"
      } from the list and require manual creation of the shipment${isSingleVehicle ? "" : "s"} and vehicle${
        isSingleVehicle === 1 ? "" : "s"
      }.`,
      onOk: async () => {
        setProcess(TenderInputFileProcess.ShipManually);
        changeStatusVehicle({
          ids: selectedVehicles.map(x => x.id),
          status: NissanVehicleStatus.ShippedManually,
        }).then(result => {
          if (!result.error) {
            message.success(`Successfully processed vehicle(s).`, 8);
          } else {
            message.error(`Failed to process vehicle(s).`, 8);
          }
          setSelectedVehicles([]);
          setLoading(false);
          actionRef?.current?.reload();
        });
      },
    });
  }, [isSingleVehicle, selectedVehicles]);

  const toolBarRender = useMemo(
    () =>
      function render() {
        const buttonsList = [];

        if (configPerm["NissanService.Vehicles"]) {
          buttonsList.push(
            <Button
              key="update-bay-locations"
              icon={<UploadOutlined />}
              type="primary"
              onClick={() => setBayLocationModalVisible(true)}
              style={{ marginRight: "1.25rem" }}
            >
              Update Bay Locations
            </Button>,
          );
        }

        buttonsList.push(
          <Button
            key="create-multiple"
            type="primary"
            style={{ marginRight: "1.25rem" }}
            onClick={createMultiVehicleShipmentConfirm}
            loading={
              process === TenderInputFileProcess.CreateMultiVehicleShipment && (loadingCreateShipment || loading)
            }
            icon={<AppstoreAddOutlined />}
            disabled={isMultiVehicleShipmentDisabled}
          >
            Create Multi-vehicle Shipment
          </Button>,
        );

        buttonsList.push(
          <Button
            key="create-shipment"
            type="primary"
            onClick={createShipments}
            loading={process === TenderInputFileProcess.CreateShipments && loading}
            icon={<PlusSquareOutlined />}
            disabled={isCreateShipmentsDisabled}
          >
            {`Create Shipment${isSingleVehicle > 1 ? "s" : ""}`}
          </Button>,
        );

        if (configPerm["NissanService.Vehicles.ShipManually"]) {
          buttonsList.push(
            <Button
              key="ship-manually"
              type="danger"
              style={{ marginLeft: "1.25rem" }}
              onClick={shipManuallyConfirm}
              loading={process === TenderInputFileProcess.ShipManually && (loadingShipManually || loading)}
              icon={<NodeIndexOutlined />}
              disabled={isShipManuallyDisabled}
            >
              Ship Manually
            </Button>,
          );
        }

        return buttonsList;
      },
    [
      configPerm,
      process,
      isSingleVehicle,
      isMultiVehicleShipmentDisabled,
      isCreateShipmentsDisabled,
      isShipManuallyDisabled,
      loading,
      loadingCreateShipment,
      loadingShipManually,
      createMultiVehicleShipmentConfirm,
      createShipments,
      shipManuallyConfirm,
    ],
  );

  useEffect(() => {
    const firstVehicle = selectedVehicles[0];

    const allUnitsSameAddress =
      selectedVehicles?.every(
        v =>
          v?.dealer?.addressLine1 === firstVehicle?.dealer?.addressLine1 &&
          v?.dealer?.addressLine2 === firstVehicle?.dealer?.addressLine2 &&
          v?.dealer?.city === firstVehicle?.dealer?.city &&
          v?.dealer?.state === firstVehicle?.dealer?.state &&
          v?.dealer?.zipCode === firstVehicle?.dealer?.zipCode &&
          v?.dealer?.name === firstVehicle?.dealer?.name,
      ) ?? false;

    const lessThanTwoVehicles = selectedVehicles.length < 2;

    const someVehicleAreShippedManuallyOrProcessed = selectedVehicles.some(vehicle =>
      [NissanVehicleStatus.ShippedManually, NissanVehicleStatus.Processed].includes(vehicle.status),
    );

    setIsSingleVehicle(lessThanTwoVehicles);
    setIsMultiVehicleShipmentDisabled(
      lessThanTwoVehicles || someVehicleAreShippedManuallyOrProcessed || !allUnitsSameAddress,
    );
    setIsShipManuallyDisabled(selectedVehicles.length < 1 || someVehicleAreShippedManuallyOrProcessed);
    setIsCreateShipmentsDisabled(selectedVehicles.length < 1 || someVehicleAreShippedManuallyOrProcessed);
  }, [selectedVehicles]);

  // this is to force the table to reload when the filters changes because of navigation without affecting the pagination or sorting
  const currentKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete("page");
    params.delete("pageSize");
    params.delete("sorter");
    return params.toString() ?? "";
  }, [location.search]);

  return (
    <ListView
      key={currentKey}
      ref={actionRef}
      filterType={ListToBeSorted.NissanTenderFile}
      columns={columns}
      sortingSeparator=" "
      request={fetchVehicleList}
      toolBarRender={toolBarRender}
      rowSelection={{
        selectedRowKeys: selectedVehicles.map(vehicle => vehicle.id),
        onChange: (_, selectedRows) => setSelectedVehicles(selectedRows ?? []),
        onSelectNone: () => {
          setSelectedVehicles([]);
        },
      }}
    >
      <Modal
        title="Bulk Update Bay Locations"
        open={bayLocationModalVisible}
        onOk={bayLocationForm.submit}
        onCancel={() => setBayLocationModalVisible(false)}
        confirmLoading={uploadingBayLocationSpreadsheet}
      >
        <UpdateBayLocationsForm formRef={bayLocationForm} setBayLocationModalVisible={setBayLocationModalVisible} />
      </Modal>
    </ListView>
  );
};

export default connect(({ loading, configs }) => ({
  configPerm: configs.configPerm,
  loadingCreateShipment: loading.effects["wwlNissan/createShipment"],
  loadingShipManually: loading.effects["wwlNissan/shipManually"],
  uploadingBayLocationSpreadsheet: loading.effects["wwlNissan/bulkUpdateBayLocations"],
}))(TenderFileInput);
